<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import ModalDeBusca from '@/components/base/modal/ModalDeBusca.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import FiscalPreviewCard from '@/components/base/fiscal/FiscalPreviewCard.vue'
import { criarConfigBuscaProduto } from '@/search-definitions/produtoSearch'
import { loteFabricacaoSearch } from '@/search-definitions/loteFabricacaoSearch'
import api from '@/services/api'
import { useNavegacao } from '@/composables/useNavegacao'

const props = defineProps({
  itens: { type: Array, default: () => [] },
  listas: { type: Object, default: () => ({}) },
  podeEditar: { type: Boolean, default: false },
  lstMoedaPadraoOrdem: { type: String, default: '' },
  idParceiroNegocioFornecedor: { type: Number, default: null },
  idOrdemCompra: { type: Number, default: null },
  /// <summary>Controla a exibição (colapso) do corpo do card. Padrão: aberto.</summary>
  accordionAberto: { type: Boolean, default: true },
  /// <summary>Dados fiscais agregados para exibição no card FiscalPreviewCard.
  /// Estrutura: { titulo, totalItens, itensComFiscal, totalImpostos, valorLiquido, valorTotal, fatorImpostos, impostos: [{chave, label, valor, aliquota}] }
  /// Quando fornecido, exibe o card de prévia fiscal abaixo da grid de itens.</summary>
  fiscalPreviewData: { type: Object, default: null },
  /// <summary>Permite editar o campo observação mesmo com a OC bloqueada (status >= EmAprovacao).</summary>
  permitirEdicaoObservacao: { type: Boolean, default: false },
  /// <summary>Percentual de tolerância do cabeçalho da OC, herdado pelos novos itens.</summary>
  percentualToleranciaCabecalho: { type: Number, default: null }
})

const emit = defineEmits(['update:itens', 'show-notification', 'update:accordionAberto'])

// ============================================================
// HELPERS
// ============================================================
const getTextoMoeda = () => {
  if (!props.listas.LstMoedaPadrao) return 'R$'
  if (!props.lstMoedaPadraoOrdem) return 'R$'
  const moeda = props.listas.LstMoedaPadrao.find(m => String(m.idListaClienteItem) === String(props.lstMoedaPadraoOrdem))
  return moeda ? moeda.textoLista : 'R$'
}

const getSiglaMoeda = () => {
  const texto = getTextoMoeda()
  const match = texto.match(/^[A-Z]{3}/)
  return match ? match[0] : 'R$'
}

const getTextoUnidade = (id) => {
  const lista = props.listas.LstUnidadeMedidaFornecedor || props.listas.LstUnidadeMedidaBase || props.listas.LstUnidadeMedidaBasica || []
  const un = lista.find(u => u.idListaClienteItem === id)
  return un ? un.textoLista : (id || '-')
}

const formatarQtd = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '0'
  const num = Number(valor)
  if (isNaN(num)) return '0'
  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
  })
}

const formatarValor = (valor) => {
  if (valor == null) return '-'
  try {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
  } catch (e) {
    return valor?.toFixed(2) ?? '-'
  }
}

const getTextoLista = (codigo, nomeLista) => {
  if (!codigo) return ''
  const lista = props.listas[nomeLista]
  if (!lista) return codigo
  const itemLista = lista.find(i => String(i.idListaClienteItem) === String(codigo))
  return itemLista ? itemLista.textoLista : codigo
}

// Saldo pendente de recebimento (soma das quantidades pedidas ainda não recebidas)
const saldoAbertoTotal = computed(() => {
  return (props.itens || []).reduce((acc, i) => {
    const pedido = Number(i.quantidadePedidaUnFornecedor) || 0
    const recebido = Number(i.quantidadeRecebidaUnFornecedor) || 0
    return acc + Math.max(0, pedido - recebido)
  }, 0)
})

// Controles de bloqueio granular para campos do fraction modal
const campoItemDesabilitado = computed(() => !props.podeEditar)
const observacaoDesabilitada = computed(() => !props.podeEditar && !props.permitirEdicaoObservacao)
const podeAlterarItem = computed(() => props.podeEditar || props.permitirEdicaoObservacao)

// Alterna o colapso do corpo do card (controlado pelo pai via v-model)
const toggleAccordion = () => {
  emit('update:accordionAberto', !props.accordionAberto)
}

// ============================================================
// CONFIGURAÇÕES DE BUSCA
// ============================================================
const configBuscaProduto = computed(() => criarConfigBuscaProduto())

// ============================================================
// ESTADO DO MODAL DE BUSCA
// ============================================================
const showBuscaProdutoModal = ref(false)
const produtoFornecedorModalRef = ref(null)

const navegarParaProdutoFornecedor = () => {
  useNavegacao().abrirRegra('PRODUTOS_FORNECEDOR', 'adicionar')
}

const handleProdutoFornecedorConfirm = (confirmed) => {
  if (confirmed) {
    navegarParaProdutoFornecedor()
  } else {
    emit('show-notification', {
      message: 'Não é possível inserir um produto na ordem de compra sem seu cadastro no Catálogo do Fornecedor (Produto no Fornecedor)',
      type: 'warning'
    })
  }
}

// ============================================================
// SELEÇÃO DE PRODUTO DO CADASTRO
// ============================================================
const handleProdutoCadastroSelecionado = async (produto) => {
  if (!produto) return
  showBuscaProdutoModal.value = false

  // Buscar dados complementares do produto
  let codigoNcm = produto.codigoNcm || ''
  let controlaLote = false
  let lstUnidadeMedidaFornecedor = ''
  let lstUnidadeMedidaBasica = ''
  let fatorConversao = 0

  try {
    const resProd = await api.get(`/Produtos/${produto.idProduto}`)
    const prodData = resProd.data
    codigoNcm = prodData.codigoNcm || codigoNcm
    lstUnidadeMedidaBasica = prodData.lstUnidadeMedidaBase || ''
    controlaLote = await verificarControlaLote(produto.idProduto)
  } catch (e) {
    console.error('Erro ao buscar dados do produto:', e)
  }

  // ============================================================
  // VALIDAÇÃO: Verifica se o produto possui cadastro ProdutoFornecedor
  // para o fornecedor da ordem de compra
  // ============================================================
  if (props.idParceiroNegocioFornecedor) {
    try {
      const res = await api.get('/ProdutoFornecedores', {
        params: {
          idProduto: produto.idProduto,
          idFornecedor: props.idParceiroNegocioFornecedor,
          pageSize: 1000
        }
      })

      if (!res.data || res.data.length === 0) {
        emit('show-notification', {
          message: 'Produto sem cadastro no Catálogo do Fornecedor (Produto-Fornecedor).',
          type: 'warning'
        })
        nextTick(() => {
          if (produtoFornecedorModalRef.value) {
            produtoFornecedorModalRef.value.show()
          }
        })
        return
      }

      // Extrai dados de conversão do ProdutoFornecedor
      const link = res.data[0]
      lstUnidadeMedidaFornecedor = link.lstUnidadeFornecedor || ''
      fatorConversao = Number(link.fatorConversao) || 0
      lstUnidadeMedidaBasica = link.lstUnidadeMedidaBaseProduto || lstUnidadeMedidaBasica
    } catch (error) {
      console.error('Erro ao verificar vínculo ProdutoFornecedor:', error)
      emit('show-notification', {
        message: 'Erro ao verificar vínculo do produto com o fornecedor.',
        type: 'error'
      })
      return
    }
  }

  // Define saldo disponível: 100 para fallback sem cota (não trava input numérico)
  const saldoFallback = 100

  // Popula o modal de alocação
  fractionModalData.value = {
    idOrdemCompraItem: null,
    idProduto: produto.idProduto,
    nomeProduto: produto.nome || produto.nomeProduto || '',
    sku: produto.sku || '',
    lstUnidadeMedidaFornecedor: lstUnidadeMedidaFornecedor,
    lstUnidadeMedidaEstoque: lstUnidadeMedidaBasica,
    lstUnidadeMedidaBasica: lstUnidadeMedidaBasica,
    fatorConversao: fatorConversao,
    quantidadePedidaUnFornecedor: 0,
    quantidadePedidaUnBasica: 0,
    valorUnitarioOriginal: 0,
    valorTotalOriginal: 0,
    valorUnitarioDesconto: 0,
    valorTotalDesconto: 0,
    valorUnitarioNegociado: 0,
    valorTotalNegociado: 0,
    valorTotalOriginalPadrao: null,
    valorTotalDescontoPadrao: null,
    valorTotalNegociadoPadrao: null,
    valorTotalOriginalGerencial: null,
    valorTotalDescontoGerencial: null,
    valorTotalNegociadoGerencial: null,
    lstMotivoDesconto: null,
    quantidadeRecebidaUnFornecedor: 0,
    observacao: '',
    percentualTolerancia: props.percentualToleranciaCabecalho,
    idLoteFabricacao: null,
    codigoNcm: codigoNcm,
    controlaLote: controlaLote,
    // Saldo fallback para o slider (não trava input numérico)
    saldoDisponivelForn: saldoFallback,
    saldoDisponivelBase: saldoFallback
  }

  showFractionModal.value = true
}

// ============================================================
// CONTROLE DE LOTE
// ============================================================
const controlaLoteCache = ref({})

const verificarControlaLote = async (idProduto) => {
  if (!idProduto) return false
  if (controlaLoteCache.value[idProduto] !== undefined) return controlaLoteCache.value[idProduto]
  try {
    const resProd = await api.get(`/Produtos/${idProduto}`)
    const idTipoProduto = resProd.data.idTipoProduto
    if (idTipoProduto) {
      const resTipo = await api.get(`/TiposProduto/${idTipoProduto}`)
      const result = !!resTipo.data.controlaLote
      controlaLoteCache.value[idProduto] = result
      return result
    }
  } catch (e) {
    console.error('Erro ao verificar ControlaLote do produto:', e)
  }
  controlaLoteCache.value[idProduto] = false
  return false
}

// ============================================================
// MODAL DE ALOCAÇÃO (FRACIONAMENTO)
// ============================================================
const showFractionModal = ref(false)
const editingItemId = ref(null)

// --- Empilhamento (z-index) via registry central ---
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? BASE_ZINDEX)

watch(showFractionModal, (aberto) => {
  if (aberto) {
    const { token, zIndex: z } = reservarZIndex()
    tokenZIndex.value = token
    zIndexReservado.value = z
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
    zIndexReservado.value = null
  }
})

onUnmounted(() => {
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})
const isUpdatingFromConversion = ref(false)

const fractionModalData = ref({
  idOrdemCompraItem: null,
  idProduto: null,
  nomeProduto: '',
  sku: '',
  lstUnidadeMedidaFornecedor: '',
  lstUnidadeMedidaEstoque: '',
  lstUnidadeMedidaBasica: '',
  fatorConversao: 0,
  quantidadePedidaUnFornecedor: 0,
  quantidadePedidaUnBasica: 0,
  valorUnitarioOriginal: 0,
  valorTotalOriginal: 0,
  valorUnitarioDesconto: 0,
  valorTotalDesconto: 0,
  valorUnitarioNegociado: 0,
  valorTotalNegociado: 0,
  valorTotalOriginalPadrao: null,
  valorTotalDescontoPadrao: null,
  valorTotalNegociadoPadrao: null,
  valorTotalOriginalGerencial: null,
  valorTotalDescontoGerencial: null,
  valorTotalNegociadoGerencial: null,
  lstMotivoDesconto: null,
  quantidadeRecebidaUnFornecedor: 0,
  observacao: '',
  percentualTolerancia: null,
  idLoteFabricacao: null,
  codigoNcm: '',
  controlaLote: false,
  // Saldo fallback para o slider (não trava input numérico)
  saldoDisponivelForn: 100,
  saldoDisponivelBase: 100
})

// Watch de conversão automática: quantidadeForn → quantidadeBase
watch(() => fractionModalData.value.quantidadePedidaUnFornecedor, (newVal) => {
  if (isUpdatingFromConversion.value) return
  const data = fractionModalData.value
  const fator = Number(data.fatorConversao)
  if (fator > 0) {
    isUpdatingFromConversion.value = true
    data.quantidadePedidaUnBasica = parseFloat((newVal * fator).toFixed(4))
    recalcularTotalLinha()
    isUpdatingFromConversion.value = false
  }
})

// Watch de conversão automática: quantidadeBase → quantidadeForn
watch(() => fractionModalData.value.quantidadePedidaUnBasica, (newVal) => {
  if (isUpdatingFromConversion.value) return
  const data = fractionModalData.value
  const fator = Number(data.fatorConversao)
  if (fator > 0) {
    isUpdatingFromConversion.value = true
    data.quantidadePedidaUnFornecedor = parseFloat((newVal / fator).toFixed(4))
    recalcularTotalLinha()
    isUpdatingFromConversion.value = false
  }
})

// Watch de valor unitário original para recalcular total e negociado
watch(() => fractionModalData.value.valorUnitarioOriginal, () => {
  recalcularNegociado()
  recalcularTotalLinha()
})

// Watch de desconto unitário para recalcular negociado e total.
// Quando o desconto é zerado/nulo, limpa o motivo (não faz mais sentido mantê-lo).
watch(() => fractionModalData.value.valorUnitarioDesconto, () => {
  recalcularNegociado()
  recalcularTotalLinha()
  const desconto = Number(fractionModalData.value.valorUnitarioDesconto) || 0
  if (desconto <= 0 && fractionModalData.value.lstMotivoDesconto) {
    fractionModalData.value.lstMotivoDesconto = null
  }
})

// Watch de valor unitário negociado para recalcular total
watch(() => fractionModalData.value.valorUnitarioNegociado, () => {
  recalcularTotalLinha()
})

// Indica se há desconto unitário informado (exige motivo de desconto)
const temDescontoUnitario = computed(() => {
  return Number(fractionModalData.value.valorUnitarioDesconto || 0) > 0
})

const recalcularNegociado = () => {
  const data = fractionModalData.value
  const original = Number(data.valorUnitarioOriginal) || 0
  const desconto = Number(data.valorUnitarioDesconto) || 0
  const negociado = Math.max(0, original - desconto)
  data.valorUnitarioNegociado = negociado
}

const recalcularTotalLinha = () => {
  const data = fractionModalData.value
  const qtd = Number(data.quantidadePedidaUnFornecedor) || 0
  const unit = Number(data.valorUnitarioNegociado) || 0
  data.valorTotalNegociado = qtd * unit
  data.valorTotalOriginal = qtd * (Number(data.valorUnitarioOriginal) || 0)
  data.valorTotalDesconto = qtd * (Number(data.valorUnitarioDesconto) || 0)
}

const ajustarQuantidade = (campo, delta) => {
  const data = fractionModalData.value
  const atual = Number(data[campo]) || 0
  const novo = Math.max(0, atual + delta)
  data[campo] = parseFloat(novo.toFixed(4))
}

const confirmarAlocacao = async () => {
  if (!podeAlterarItem.value) {
    emit('show-notification', { message: 'A Ordem de Compra está bloqueada para edição.', type: 'warning' })
    return
  }
  const data = fractionModalData.value
  if (data.quantidadePedidaUnFornecedor <= 0 && data.quantidadePedidaUnBasica <= 0) {
    emit('show-notification', { message: 'Informe pelo menos uma quantidade maior que zero.', type: 'warning' })
    return
  }

  // Regra de negócio: desconto exige motivo; sem desconto (0/null) o motivo é opcional
  if (temDescontoUnitario.value && !data.lstMotivoDesconto) {
    emit('show-notification', { message: 'Informe o motivo do desconto para aplicar desconto no item.', type: 'warning' })
    return
  }

  // Valida PercentualTolerancia obrigatório (pode ser 0, mas não null)
  if (data.percentualTolerancia === null || data.percentualTolerancia === undefined) {
    emit('show-notification', { message: 'A Tolerância Quantitativa (%) é obrigatória para o item.', type: 'warning' })
    return
  }

  const payload = {
    idOrdemCompra: props.idOrdemCompra,
    idProduto: data.idProduto,
    idLoteFabricacao: data.idLoteFabricacao || null,
    lstUnidadeMedidaFornecedor: data.lstUnidadeMedidaFornecedor || '',
    quantidadePedidaUnFornecedor: data.quantidadePedidaUnFornecedor || 0,
    lstUnidadeMedidaEstoque: data.lstUnidadeMedidaEstoque || data.lstUnidadeMedidaBasica || '',
    fatorConversao: data.fatorConversao || 0,
    quantidadePedidaUnBasica: data.quantidadePedidaUnBasica || 0,
    valorUnitarioOriginal: data.valorUnitarioOriginal ?? null,
    valorTotalOriginal: data.valorTotalOriginal ?? null,
    valorUnitarioDesconto: data.valorUnitarioDesconto ?? null,
    valorTotalDesconto: data.valorTotalDesconto ?? null,
    valorUnitarioNegociado: data.valorUnitarioNegociado ?? null,
    valorTotalNegociado: data.valorTotalNegociado ?? null,
    valorTotalOriginalPadrao: data.valorTotalOriginalPadrao ?? null,
    valorTotalDescontoPadrao: data.valorTotalDescontoPadrao ?? null,
    valorTotalNegociadoPadrao: data.valorTotalNegociadoPadrao ?? null,
    valorTotalOriginalGerencial: data.valorTotalOriginalGerencial ?? null,
    valorTotalDescontoGerencial: data.valorTotalDescontoGerencial ?? null,
    valorTotalNegociadoGerencial: data.valorTotalNegociadoGerencial ?? null,
    lstMotivoDesconto: data.lstMotivoDesconto ?? null,
    quantidadeRecebidaUnFornecedor: 0,
    percentualTolerancia: data.percentualTolerancia ?? null,
    observacao: data.observacao || null
  }

  try {
    if (editingItemId.value) {
      await api.put(`/OrdensCompraItens/${editingItemId.value}`, payload)
      emit('show-notification', { message: 'Item atualizado com sucesso!', type: 'success' })
    } else {
      await api.post('/OrdensCompraItens', payload)
      emit('show-notification', { message: 'Item adicionado com sucesso!', type: 'success' })
    }

    showFractionModal.value = false
    editingItemId.value = null
    await carregarItens()
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao salvar item da ordem de compra.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

const fecharModal = () => {
  showFractionModal.value = false
  editingItemId.value = null
}

const handleModalKeydown = (e) => {
  if (e.key === 'Enter' && showFractionModal.value) {
    if (!modalAlocacaoRef.value?.contains(e.target)) return
    e.preventDefault()
    confirmarAlocacao()
  }
}

watch(() => showFractionModal.value, (isOpen) => {
  if (isOpen) {
    document.addEventListener('keydown', handleModalKeydown)
  } else {
    document.removeEventListener('keydown', handleModalKeydown)
  }
})

// ============================================================
// EDIÇÃO E EXCLUSÃO DE ITENS
// ============================================================
const abrirEdicao = async (item) => {
  // Permite abrir o modal mesmo sem podeEditar (consulta); o bloqueio é granular nos campos
  editingItemId.value = item.idOrdemCompraItem

  let controlaLote = false
  if (item.idProduto) {
    controlaLote = await verificarControlaLote(item.idProduto)
  }

  fractionModalData.value = {
    idOrdemCompraItem: item.idOrdemCompraItem,
    idProduto: item.idProduto,
    nomeProduto: item.nomeProduto || '',
    sku: item.sku || '',
    lstUnidadeMedidaFornecedor: item.lstUnidadeMedidaFornecedor || '',
    lstUnidadeMedidaEstoque: item.lstUnidadeMedidaEstoque || item.lstUnidadeMedidaBasica || '',
    lstUnidadeMedidaBasica: item.lstUnidadeMedidaBasica || item.lstUnidadeMedidaEstoque || '',
    fatorConversao: Number(item.fatorConversao) || 0,
    quantidadePedidaUnFornecedor: Number(item.quantidadePedidaUnFornecedor) || 0,
    quantidadePedidaUnBasica: Number(item.quantidadePedidaUnBasica) || 0,
    valorUnitarioOriginal: item.valorUnitarioOriginal ?? null,
    valorTotalOriginal: item.valorTotalOriginal ?? null,
    valorUnitarioDesconto: item.valorUnitarioDesconto ?? null,
    valorTotalDesconto: item.valorTotalDesconto ?? null,
    valorUnitarioNegociado: item.valorUnitarioNegociado ?? null,
    valorTotalNegociado: item.valorTotalNegociado ?? null,
    valorTotalOriginalPadrao: item.valorTotalOriginalPadrao ?? null,
    valorTotalDescontoPadrao: item.valorTotalDescontoPadrao ?? null,
    valorTotalNegociadoPadrao: item.valorTotalNegociadoPadrao ?? null,
    valorTotalOriginalGerencial: item.valorTotalOriginalGerencial ?? null,
    valorTotalDescontoGerencial: item.valorTotalDescontoGerencial ?? null,
    valorTotalNegociadoGerencial: item.valorTotalNegociadoGerencial ?? null,
    lstMotivoDesconto: item.lstMotivoDesconto ?? null,
    quantidadeRecebidaUnFornecedor: Number(item.quantidadeRecebidaUnFornecedor) || 0,
    observacao: item.observacao || '',
    percentualTolerancia: item.percentualTolerancia ?? null,
    idLoteFabricacao: item.idLoteFabricacao || null,
    codigoNcm: item.codigoNcm || '',
    controlaLote: controlaLote,
    // Saldo fallback para o slider (não trava input numérico)
    saldoDisponivelForn: 100,
    saldoDisponivelBase: 100
  }

  showFractionModal.value = true
}

const carregarItens = async () => {
  if (!props.idOrdemCompra) return
  try {
    const res = await api.get('/OrdensCompraItens', {
      params: { idOrdemCompra: props.idOrdemCompra, pageSize: 999 }
    })
    const itensCarregados = res.data || []
    emit('update:itens', itensCarregados)
  } catch (e) {
    console.error('Erro ao carregar itens da ordem:', e)
    emit('show-notification', { message: 'Erro ao carregar itens da ordem.', type: 'error' })
  }
}

const removerItem = async (index) => {
  const item = props.itens[index]
  if (!item || !item.idOrdemCompraItem) {
    emit('show-notification', { message: 'Item não encontrado para exclusão.', type: 'error' })
    return
  }
  try {
    await api.delete(`/OrdensCompraItens/${item.idOrdemCompraItem}`)
    emit('show-notification', { message: 'Item removido com sucesso!', type: 'success' })
    await carregarItens()
  } catch (e) {
    const errorMsg = e.response?.data?.message || 'Erro ao remover item.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  }
}

// Config de busca de lote dinâmica
const configBuscaLote = computed(() => {
  const base = { ...loteFabricacaoSearch }
  const idFornecedor = fractionModalData.value.idParceiroNegocioProdutor || props.idParceiroNegocioFornecedor
  if (idFornecedor) {
    base.filtrosFixos = { ...(base.filtrosFixos || {}), idFornecedor }
  } else {
    base.filtrosFixos = { ...(base.filtrosFixos || {}), idFornecedor: -1 }
    base.titulo = 'Selecione um fornecedor primeiro'
  }
  return base
})
</script>

<template>
  <div class="ordem-compra-itens-smart-mode">
    <!-- ============================================================ -->
    <!-- TOOLBAR -->
    <!-- ============================================================ -->
    <div class="itens-main-toolbar cursor-pointer" @click="toggleAccordion">
      <h5 class="mb-0 d-flex align-items-center gap-2">
        <i class="bi bi-list-check"></i>
        Produtos da Ordem de Compra
        <span v-if="itens.length > 0"
          class="badge bg-primary bg-opacity-10 text-primary-emphasis border border-primary-subtle ms-1">
          {{ itens.length }} {{ itens.length === 1 ? 'produto' : 'produtos' }}
        </span>
        <span v-if="itens.length > 0" class="badge bg-warning text-dark ms-1" title="Saldo pendente de recebimento">
          <i class="bi bi-hourglass-split me-1"></i>
          Saldo: {{ formatarQtd(saldoAbertoTotal) }}
        </span>
      </h5>
      <div class="d-flex gap-2 align-items-center">
        <button v-if="podeEditar" type="button" class="btn btn-sm btn-primary text-white fw-bold"
          @click.stop="showBuscaProdutoModal = true"
          title="Adicionar Produto do Cadastro">
          <i class="bi bi-search me-1"></i> Adicionar Produto do Cadastro
        </button>
        <i :class="['bi', accordionAberto ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- MODAL DE BUSCA DE PRODUTO -->
    <!-- ============================================================ -->
    <ModalDeBusca
      v-if="showBuscaProdutoModal"
      :configuracao="configBuscaProduto"
      tamanho="lg"
      @item-selecionado="handleProdutoCadastroSelecionado"
      @fechar="showBuscaProdutoModal = false"
    />

    <!-- ============================================================ -->
    <!-- CORPO: CARDS DOS ITENS ALOCADOS -->
    <!-- ============================================================ -->
    <div class="itens-main-body" v-show="accordionAberto">
      <template v-if="itens.length === 0">
        <div class="empty-state">
          <i class="bi bi-cart-plus fs-1 d-block mb-2 text-muted"></i>
          <p class="mb-0">Nenhum produto adicionado.</p>
          <p v-if="podeEditar" class="small text-muted">
            Clique em <strong>"Adicionar Produto do Cadastro"</strong> para começar.
          </p>
        </div>
      </template>

      <template v-else>
        <div class="itens-grid">
          <div v-for="(item, index) in itens" :key="item.idOrdemCompraItem || index" class="item-card">
            <!-- Header do Card -->
            <div class="item-card-header">
              <div class="item-card-info">
                <div class="item-card-nome">
                  <code class="sku-destaque" v-if="item.sku">{{ item.sku }}</code>
                  <span class="produto-nome-texto">{{ item.nomeProduto || 'Produto #' + item.idProduto }}</span>
                </div>
              </div>
              <div class="item-card-actions">
                <button type="button" class="btn btn-sm btn-outline-primary border-0" title="Editar"
                  @click="abrirEdicao(item)">
                  <i class="bi bi-pencil"></i>
                </button>
                <button v-if="podeEditar" type="button" class="btn btn-sm btn-outline-danger border-0" title="Remover"
                  @click="removerItem(index)">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>

            <!-- Código NCM -->
            <div class="item-card-codigos-fiscais" v-if="item.codigoNcm">
              <span class="codigo-fiscal-badge">
                NCM: <code>{{ item.codigoNcm }}</code>
              </span>
            </div>

            <!-- Detalhes -->
            <div class="item-card-detalhes">
              <div class="detalhe-item">
                <span class="detalhe-label">Saldo Disponível (Unidade do Fornecedor):</span>
                <span class="detalhe-value">{{ formatarQtd(item.quantidadePedidaUnFornecedor) }} {{ getTextoUnidade(item.lstUnidadeMedidaFornecedor) }}</span>
              </div>
              <div class="detalhe-item">
                <span class="detalhe-label">Saldo Disponível (Unidade Base):</span>
                <span class="detalhe-value">{{ formatarQtd(item.quantidadePedidaUnBasica) }} {{ getTextoUnidade(item.lstUnidadeMedidaBasica || item.lstUnidadeMedidaEstoque) }}</span>
              </div>
              <div class="detalhe-item">
                <span class="detalhe-label">Preço Unit. Orig.:</span>
                <span class="detalhe-value">{{ formatarValor(item.valorUnitarioOriginal) }}</span>
              </div>
              <div class="detalhe-item" v-if="Number(item.valorUnitarioDesconto || 0) > 0">
                <span class="detalhe-label">Desc. Unit.:</span>
                <span class="detalhe-value text-danger">-{{ formatarValor(item.valorUnitarioDesconto) }}</span>
              </div>
              <div class="detalhe-item">
                <span class="detalhe-label">Preço Unit. Negoc.:</span>
                <span class="detalhe-value fw-bold">{{ formatarValor(item.valorUnitarioNegociado) }}</span>
              </div>
              <div class="detalhe-item" v-if="item.lstMotivoDesconto">
                <span class="detalhe-label">Motivo Desc.:</span>
                <span class="detalhe-value"><small>{{ getTextoLista(item.lstMotivoDesconto, 'LstMotivoDesconto') || item.lstMotivoDesconto }}</small></span>
              </div>
              <div class="detalhe-item detalhe-total">
                <span class="detalhe-label">Total Negoc.:</span>
                <span class="detalhe-value fw-bold">{{ formatarValor(item.valorTotalNegociado) }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Card de prévia fiscal agregada (opcional, via prop fiscalPreviewData) -->
    <div v-if="fiscalPreviewData && accordionAberto" class="mt-3">
      <FiscalPreviewCard
        :titulo="fiscalPreviewData.titulo || 'Prévia dos Impostos'"
        :origem="fiscalPreviewData._origem || 'simulacao'"
        :loading="false"
        :total-itens="fiscalPreviewData.totalItens || 0"
        :itens-com-fiscal="fiscalPreviewData.itensComFiscal || 0"
        :impostos="fiscalPreviewData.impostos || []"
        :total-impostos="fiscalPreviewData.totalImpostos || 0"
        :valor-liquido="fiscalPreviewData.valorLiquido || 0"
        :valor-total="fiscalPreviewData.valorTotal || 0"
        :fator-impostos="fiscalPreviewData.fatorImpostos || 0"
      />
    </div>

    <!-- ============================================================ -->
    <!-- MODAL DE ALOCAÇÃO (FRACIONAMENTO) -->
    <!-- ============================================================ -->
    <Teleport to="body">
      <div v-if="showFractionModal" ref="modalAlocacaoRef" class="modal-overlay" :style="{ zIndex: zIndexEfetivo }" @click.self="fecharModal">
        <div class="modal-card">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi" :class="editingItemId ? 'bi-pencil' : 'bi-arrows-split'"></i>
              <span class="me-1">{{ editingItemId ? 'Editar' : 'Alocar' }}</span>
              Produto na Ordem de Compra
            </h5>
            <button type="button" class="btn-close" @click="fecharModal"></button>
          </div>

          <div class="modal-body">
            <!-- Info do Produto -->
            <div class="modal-info-box">
              <div class="info-left">
                <div class="info-item produto-info-item">
                  <span class="info-label">Produto:</span>
                  <span class="info-valor fw-bold">
                    <template v-if="fractionModalData.sku">
                      <code class="sku-destaque d-block">{{ fractionModalData.sku }}</code>
                    </template>
                    {{ fractionModalData.nomeProduto }}
                  </span>
                </div>
                <div class="info-item" v-if="fractionModalData.codigoNcm">
                  <span class="info-label">NCM:</span>
                  <span class="info-valor fw-bold">
                    <code style="font-size:0.75rem;">{{ fractionModalData.codigoNcm }}</code>
                  </span>
                </div>
              </div>
              <div class="info-right">
                <div class="info-item">
                  <span class="info-label">Saldo Disponível (Unidade do Fornecedor):</span>
                  <span class="info-valor">{{ getTextoUnidade(fractionModalData.lstUnidadeMedidaFornecedor) || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Saldo Disponível (Unidade Base):</span>
                  <span class="info-valor">{{ getTextoUnidade(fractionModalData.lstUnidadeMedidaBasica) || '-' }}</span>
                </div>
              </div>
            </div>

            <!-- Aviso de conversão -->
            <div v-if="Number(fractionModalData.fatorConversao) > 0"
              class="alert alert-info py-2 mb-3" style="font-size:0.8rem;">
              <i class="bi bi-info-circle me-2"></i>
              Fator de conversão: <strong>{{ fractionModalData.fatorConversao }}</strong>.
              As quantidades são sincronizadas automaticamente.
            </div>
            <div v-else
              class="alert alert-warning py-2 mb-3" style="font-size:0.8rem;">
              <i class="bi bi-exclamation-triangle me-2"></i>
              Fator de conversão zero ou não definido. As quantidades devem ser informadas independentemente.
            </div>

            <!-- Sliders de Quantidade -->
            <div class="sliders-stacked">
              <!-- Qtd Fornecedor -->
              <div class="slider-section">
                <div class="slider-section-header">
                  <i class="bi bi-box-seam me-1"></i>
                  Qtd. Fornecedor
                  <small class="text-muted">({{ getTextoUnidade(fractionModalData.lstUnidadeMedidaFornecedor) }})</small>
                </div>
                <div class="slider-row">
                  <input type="range" class="form-range slider-row-main" :min="0"
                    :max="fractionModalData.saldoDisponivelForn" step="1"
                    v-model.number="fractionModalData.quantidadePedidaUnFornecedor"
                    :disabled="campoItemDesabilitado" />
                  <div class="slider-row-controls">
                    <div class="slider-row-buttons">
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Diminuir 1" @click="ajustarQuantidade('quantidadePedidaUnFornecedor', -1)"
                        :disabled="campoItemDesabilitado || fractionModalData.quantidadePedidaUnFornecedor < 1">
                        <i class="bi bi-dash-lg"></i> 1
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Diminuir 0,1" @click="ajustarQuantidade('quantidadePedidaUnFornecedor', -0.1)"
                        :disabled="campoItemDesabilitado || fractionModalData.quantidadePedidaUnFornecedor < 0.1">
                        <i class="bi bi-dash-lg"></i> 0,1
                      </button>
                    </div>
                    <div class="slider-row-value">
                      <input type="number" class="form-control form-control-sm" :min="0" step="0.01"
                        v-model.number="fractionModalData.quantidadePedidaUnFornecedor"
                        :disabled="campoItemDesabilitado" />
                      <span class="slider-max">/ {{ formatarQtd(fractionModalData.saldoDisponivelForn) }}</span>
                    </div>
                    <div class="slider-row-buttons">
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Aumentar 1" @click="ajustarQuantidade('quantidadePedidaUnFornecedor', 1)"
                        :disabled="campoItemDesabilitado">
                        <i class="bi bi-plus-lg"></i> 1
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Aumentar 0.1" @click="ajustarQuantidade('quantidadePedidaUnFornecedor', 0.1)"
                        :disabled="campoItemDesabilitado">
                        <i class="bi bi-plus-lg"></i> 0,1
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Qtd Básica -->
              <div class="slider-section">
                <div class="slider-section-header">
                  <i class="bi bi-box me-1"></i>
                  Qtd. Básica (Estoque)
                  <small class="text-muted">({{ getTextoUnidade(fractionModalData.lstUnidadeMedidaBasica) }})</small>
                </div>
                <div class="slider-row">
                  <input type="range" class="form-range slider-row-main" :min="0"
                    :max="fractionModalData.saldoDisponivelBase" step="1"
                    v-model.number="fractionModalData.quantidadePedidaUnBasica"
                    :disabled="campoItemDesabilitado" />
                  <div class="slider-row-controls">
                    <div class="slider-row-buttons">
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Diminuir 1" @click="ajustarQuantidade('quantidadePedidaUnBasica', -1)"
                        :disabled="campoItemDesabilitado || fractionModalData.quantidadePedidaUnBasica < 1">
                        <i class="bi bi-dash-lg"></i> 1
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Diminuir 0,1" @click="ajustarQuantidade('quantidadePedidaUnBasica', -0.1)"
                        :disabled="campoItemDesabilitado || fractionModalData.quantidadePedidaUnBasica < 0.1">
                        <i class="bi bi-dash-lg"></i> 0,1
                      </button>
                    </div>
                    <div class="slider-row-value">
                      <input type="number" class="form-control form-control-sm" :min="0" step="0.01"
                        v-model.number="fractionModalData.quantidadePedidaUnBasica"
                        :disabled="campoItemDesabilitado" />
                      <span class="slider-max">/ {{ formatarQtd(fractionModalData.saldoDisponivelBase) }}</span>
                    </div>
                    <div class="slider-row-buttons">
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn"
                        title="Aumentar 1" @click="ajustarQuantidade('quantidadePedidaUnBasica', 1)"
                        :disabled="campoItemDesabilitado">
                        <i class="bi bi-plus-lg"></i> 1
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-secondary slider-btn slider-btn-decimal"
                        title="Aumentar 0.1" @click="ajustarQuantidade('quantidadePedidaUnBasica', 0.1)"
                        :disabled="campoItemDesabilitado">
                        <i class="bi bi-plus-lg"></i> 0,1
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Preço Original, Desconto e Valor Total -->
            <div class="campos-preco mt-3">
              <div class="row g-2">
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                    Preço Unit. Original ({{ getSiglaMoeda() }})
                  </label>
                  <CampoInput id="modal-precoUnitarioOriginal" label="" :model-value="fractionModalData.valorUnitarioOriginal"
                    @update:model-value="fractionModalData.valorUnitarioOriginal = $event" type="moeda" hide-label
                    :disabled="campoItemDesabilitado" />
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                    Desconto Unit. ({{ getSiglaMoeda() }})
                  </label>
                  <CampoInput id="modal-precoUnitarioDesconto" label="" :model-value="fractionModalData.valorUnitarioDesconto"
                    @update:model-value="fractionModalData.valorUnitarioDesconto = $event" type="moeda" hide-label
                    :disabled="campoItemDesabilitado" />
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                    Preço Unit. Negoc. ({{ getSiglaMoeda() }})
                  </label>
                  <CampoInput id="modal-precoUnitarioNegociado" label=""
                    :model-value="fractionModalData.valorUnitarioNegociado" type="moeda"
                    hide-label disabled />
                </div>
              </div>
              <div class="row g-2 mt-2">
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                    Total Original ({{ getSiglaMoeda() }})
                  </label>
                  <CampoInput id="modal-totalOriginal" label=""
                    :model-value="fractionModalData.valorTotalOriginal" type="moeda"
                    hide-label disabled />
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                    Total Desconto ({{ getSiglaMoeda() }})
                  </label>
                  <CampoInput id="modal-totalDesconto" label=""
                    :model-value="fractionModalData.valorTotalDesconto" type="moeda"
                    hide-label disabled />
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                    Total Negociado ({{ getSiglaMoeda() }})
                  </label>
                  <CampoInput id="modal-totalNegociado" label=""
                    :model-value="fractionModalData.valorTotalNegociado" type="moeda"
                    hide-label disabled />
                </div>
              </div>
            </div>

            <!-- Motivo do Desconto -->
            <div class="mt-3">
              <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                Motivo do Desconto
              </label>
              <CampoBuscaLista
                id="lstMotivoDesconto"
                v-model="fractionModalData.lstMotivoDesconto"
                tabela="OrdemCompraItem"
                hide-label
                :required="temDescontoUnitario"
                :disabled="campoItemDesabilitado || !temDescontoUnitario"
              />
            </div>

            <!-- Lote de Fabricação (visível apenas se o produto controla lote) -->
            <div v-if="fractionModalData.controlaLote" class="mt-3">
              <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                Lote de Fabricação
              </label>
              <CampoBusca
                id="modal-idLoteFabricacao"
                v-model="fractionModalData.idLoteFabricacao"
                :configuracao-busca="configBuscaLote"
                :disabled="campoItemDesabilitado"
                hide-label
              />
            </div>

            <!-- Tolerância -->
            <div class="mt-3">
              <CampoInput
                id="percentualTolerancia"
                label="Tolerância Quantitativa (%)"
                type="percentual"
                v-model="fractionModalData.percentualTolerancia"
                required
                :disabled="campoItemDesabilitado"
              />
            </div>

            <!-- Observação -->
            <div class="mt-3">
              <label class="form-label fw-bold text-secondary mb-0" style="font-size: 0.8rem;">
                Observação
              </label>
              <textarea class="form-control form-control-sm" rows="2"
                v-model="fractionModalData.observacao"
                :disabled="observacaoDesabilitada"
                placeholder="Observação do item..."></textarea>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="fecharModal">
              Cancelar
            </button>
            <button v-if="podeAlterarItem" type="button" class="btn btn-primary text-white fw-bold" @click="confirmarAlocacao" autofocus>
              <i class="bi bi-check-circle me-1"></i>
              {{ editingItemId ? 'Salvar Alterações' : 'Confirmar Alocação' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================================ -->
    <!-- ConfirmationModal para Produto sem cadastro no Fornecedor -->
    <!-- ============================================================ -->
    <Teleport to="body">
      <ConfirmationModal
        ref="produtoFornecedorModalRef"
        title="Produto sem Cadastro no Fornecedor"
        message="Este produto não possui cadastro no Catálogo do Fornecedor (Produto-Fornecedor). Deseja ir para o cadastro?"
        confirm-button-text="Sim, ir para cadastro"
        cancel-button-text="Não, cancelar"
        @confirmed="handleProdutoFornecedorConfirm"
      />
    </Teleport>
  </div>
</template>

<style scoped>
.ordem-compra-itens-smart-mode {
  position: relative;
  width: 100%;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1.5rem;
}

/* ============================================================ */
/* TOOLBAR */
/* ============================================================ */
.itens-main-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--bs-primary-bg-subtle);
  border-bottom: 1px solid var(--bs-primary-border-subtle);
}

.itens-main-toolbar h5 {
  font-size: 0.9rem;
  font-weight: 700;
  margin: 0;
}

/* ============================================================ */
/* BODY */
/* ============================================================ */
.itens-main-body {
  padding: 12px;
  min-height: 120px;
}

.empty-state {
  text-align: center;
  padding: 40px 16px;
  color: var(--bs-secondary-color);
}

.empty-state p {
  font-size: 0.85rem;
}

/* ============================================================ */
/* CARDS GRID */
/* ============================================================ */
.itens-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.item-card {
  padding: 10px 12px;
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-primary);
  border-radius: 6px;
  background: var(--bs-body-bg);
}

.item-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
}

.item-card-info {
  flex: 1;
  min-width: 0;
}

.item-card-nome {
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--bs-body-color);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.sku-destaque {
  color: var(--bs-danger);
  font-weight: 700;
  font-size: 0.75rem;
}

.produto-nome-texto {
  font-weight: 600;
  font-size: 0.85rem;
}

.item-card-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.item-card-codigos-fiscais {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.codigo-fiscal-badge {
  font-size: 0.65rem;
  background: var(--bs-tertiary-bg);
  padding: 1px 6px;
  border-radius: 4px;
  color: var(--bs-secondary-color);
}

.codigo-fiscal-badge code {
  font-weight: 600;
  color: var(--bs-info-text-emphasis);
  font-size: 0.7rem;
}

.item-card-detalhes {
  font-size: 0.75rem;
}

.detalhe-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.detalhe-label {
  color: var(--bs-secondary-color);
  font-size: 0.72rem;
}

.detalhe-value {
  font-weight: 600;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.75rem;
}

.detalhe-total {
  border-top: 1px solid var(--bs-border-color-translucent);
  margin-top: 4px;
  padding-top: 4px;
}

/* ============================================================ */
/* MODAL OVERLAY */
/* ============================================================ */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
}

.modal-card {
  background: var(--bs-body-bg);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: calc(100% - 32px);
  max-width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 16px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #34495E;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.modal-header .btn-close {
  filter: invert(1);
}

.modal-title {
  font-weight: 700;
  font-size: 1rem;
  margin: 0;
  color: #fff;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid var(--bs-border-color);
  background: var(--bs-tertiary-bg);
}

/* ============================================================ */
/* MODAL INFO BOX */
/* ============================================================ */
.modal-info-box {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 24px;
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.info-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-right {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: right;
}

.info-item {
  display: flex;
  gap: 8px;
  font-size: 0.85rem;
}

.info-item .info-label {
  font-weight: 600;
  min-width: 70px;
  color: var(--bs-secondary-color);
  white-space: nowrap;
}

.info-item .info-valor {
  color: var(--bs-body-color);
}

.sku-destaque {
  color: var(--bs-danger);
  font-weight: 700;
}

/* ============================================================ */
/* SLIDERS */
/* ============================================================ */
.sliders-stacked {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.slider-section {
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
}

.slider-section-header {
  font-weight: 700;
  font-size: 0.8rem;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.slider-section-header small {
  font-weight: 400;
  font-size: 0.7rem;
}

.slider-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.slider-row-main {
  flex: 1;
  min-width: 0;
  margin: 0;
}

.slider-row-controls {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
}

.slider-row-buttons {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.slider-row-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  min-width: 110px;
}

.slider-row-value .form-control-sm {
  width: 100%;
  font-family: 'Consolas', 'Courier New', monospace;
  font-weight: 600;
  text-align: center;
}

.slider-row-value .slider-max {
  font-size: 0.65rem;
  color: var(--bs-secondary-color);
  font-family: 'Consolas', 'Courier New', monospace;
  white-space: nowrap;
}

.slider-btn {
  font-size: 0.6rem;
  padding: 2px 8px;
  line-height: 1.3;
  border-width: 1px;
  white-space: nowrap;
  min-width: 36px;
}

.slider-btn-decimal {
  font-size: 0.55rem;
  opacity: 0.8;
  min-width: 32px;
}

/* ============================================================ */
/* CAMPOS PREÇO */
/* ============================================================ */
.campos-preco {
  background: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  padding: 12px;
}

/* ============================================================ */
/* RESPONSIVIDADE */
/* ============================================================ */
@media (max-width: 1400px) {
  .itens-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1200px) {
  .itens-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .itens-grid {
    grid-template-columns: 1fr;
  }
}
</style>
